// src/components/onboarding/opening-hours-picker.tsx
export interface DayHours { closed: boolean; open: string; close: string }
export type OpeningHours = Record<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday', DayHours>;
import { useAppColors } from '@/src/lib/theme';

export const DEFAULT_HOURS: OpeningHours = {
  monday:    { closed: false, open: '09:00', close: '22:00' },
  tuesday:   { closed: false, open: '09:00', close: '22:00' },
  wednesday: { closed: false, open: '09:00', close: '22:00' },
  thursday:  { closed: false, open: '09:00', close: '22:00' },
  friday:    { closed: false, open: '09:00', close: '23:00' },
  saturday:  { closed: false, open: '09:00', close: '23:00' },
  sunday:    { closed: true,  open: '09:00', close: '22:00' },
};

// Fills in any missing days with the default — handles null, {}, or a
// partial object from the DB without ever leaving a day undefined.
export function normalizeOpeningHours(value: Partial<OpeningHours> | null | undefined): OpeningHours {
  const days = Object.keys(DEFAULT_HOURS) as Array<keyof OpeningHours>;
  const result = {} as OpeningHours;
  for (const day of days) {
    result[day] = value?.[day] ?? DEFAULT_HOURS[day];
  }
  return result;
}

const DAY_LABELS: Record<keyof OpeningHours, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

interface OpeningHoursPickerProps {
  value: OpeningHours;
  onChange: (value: OpeningHours) => void;
}

export default function OpeningHoursPicker({ value, onChange }: OpeningHoursPickerProps) {
  const c = useAppColors();
  const days = Object.keys(DAY_LABELS) as Array<keyof OpeningHours>;
  const safeValue = normalizeOpeningHours(value);

  function updateDay(day: keyof OpeningHours, patch: Partial<DayHours>) {
    onChange({ ...safeValue, [day]: { ...safeValue[day], ...patch } });
  }

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: c.textFaint }}>Opening hours</label>
      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${c.border}`, background: c.iconBg }}>
        {days.map((day, i) => {
          const d = safeValue[day];
          return (
            <div
              key={day}
              className="flex items-center gap-3 px-3 py-2.5"
              style={i > 0 ? { borderTop: `1px solid ${c.border}` } : undefined}
            >
              <span className="w-9 text-[11px] font-semibold flex-shrink-0" style={{ color: c.textFaint }}>{DAY_LABELS[day]}</span>
              <button
                type="button"
                onClick={() => updateDay(day, { closed: !d.closed })}
                className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full flex-shrink-0"
                style={d.closed ? { background: c.border, color: c.textFaint } : { background: 'rgba(74,154,74,0.18)', color: '#4A9A4A' }}
              >
                {d.closed ? 'Closed' : 'Open'}
              </button>
              {!d.closed && (
                <div className="flex items-center gap-1.5 flex-1 justify-end">
                  <input
                    type="time"
                    value={d.open}
                    onChange={(e) => updateDay(day, { open: e.target.value })}
                    className="bg-transparent text-[12px] rounded-lg px-2 py-1 focus:outline-none"
                    style={{ color: c.text, border: `1px solid ${c.border}` }}
                  />
                  <span className="text-[11px]" style={{ color: c.textFaint }}>–</span>
                  <input
                    type="time"
                    value={d.close}
                    onChange={(e) => updateDay(day, { close: e.target.value })}
                    className="bg-transparent text-[12px] rounded-lg px-2 py-1 focus:outline-none"
                    style={{ color: c.text, border: `1px solid ${c.border}` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}